/**
 * MSAL Configuration for Azure AD Authentication
 * 
 * This file contains all the configuration needed for Azure AD SSO integration.
 * Environment variables are loaded from .env file with VITE_ prefix.
 */

import { Configuration, LogLevel, RedirectRequest, PopupRequest } from '@azure/msal-browser'
import { isVerboseLogging } from '../utils/logger'

// Azure AD Configuration from environment variables
// Uses VITE_AZURE_AD_* naming convention to match backend (.env) naming
const AZURE_AD_TENANT_ID = import.meta.env.VITE_AZURE_AD_TENANT_ID || ''
const AZURE_AD_CLIENT_ID = import.meta.env.VITE_AZURE_AD_CLIENT_ID || ''
const REDIRECT_URI = import.meta.env.VITE_AZURE_AD_REDIRECT_URI || window.location.origin

// Check if Azure SSO is configured
export const isAzureSSOConfigured = (): boolean => {
  return !!(AZURE_AD_TENANT_ID && AZURE_AD_CLIENT_ID)
}

/**
 * MSAL Configuration object
 * See: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: AZURE_AD_CLIENT_ID,
    // Azure AD v2.0 endpoint
    authority: `https://login.microsoftonline.com/${AZURE_AD_TENANT_ID}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
    // Required for SPA auth code flow
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // Use sessionStorage for better security (cleared when tab closes)
    cacheLocation: 'sessionStorage',
    // Set to true for apps with multiple users
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) return
        // Only produce info/verbose output when verbose logging is enabled
        const verbose = isVerboseLogging()
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message)
            break
          case LogLevel.Warning:
            console.warn('[MSAL]', message)
            break
          case LogLevel.Info:
            if (verbose) console.info('[MSAL]', message)
            break
          case LogLevel.Verbose:
            if (verbose) console.debug('[MSAL]', message)
            break
        }
      },
      // Only info/verbose when explicitly allowed (localhost or VITE flag), otherwise warn
      logLevel: isVerboseLogging() ? LogLevel.Info : LogLevel.Warning,
      piiLoggingEnabled: false,
    },
    // Allow navigation to different domains
    allowNativeBroker: false,
  },
}

/**
 * Scopes for ID token - OpenID Connect scopes
 * We only need the id_token for authentication, not access token
 */
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email'],
  // Prompt user to select account if multiple accounts are signed in
  prompt: 'select_account',
}

/**
 * Popup login request (fallback for redirect issues)
 */
export const loginPopupRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email'],
  prompt: 'select_account',
}

/**
 * API endpoint for the backend bridge
 */
export const API_BRIDGE_ENDPOINT = '/auth/azure/bridge/'

/**
 * Get the Azure AD logout URL
 */
export const getLogoutUrl = (): string => {
  const encodedRedirectUri = encodeURIComponent(REDIRECT_URI)
  return `https://login.microsoftonline.com/${AZURE_AD_TENANT_ID}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodedRedirectUri}`
}
